import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, InputAdornment, Stack, TextField,
  ToggleButton, ToggleButtonGroup, Typography, alpha, useTheme,
} from '@mui/material';
import { ArrowForward } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import feeInvoiceService from '../../services/feeInvoiceService';
import { FeeRequestLimits } from '../../types/finance';
import { useAuth } from '../../context/AuthContext';
import { useCurrencyFormat } from '../../hooks/useCurrencyFormat';

export type ChallanAdjustmentMode = 'charge' | 'correction';

const COPY: Record<ChallanAdjustmentMode, { title: string; hint: string; reasonLabel: string; placeholder: string }> = {
  charge: {
    title: 'Add a charge',
    hint: 'Something extra billed on this challan. It is booked as other fee income.',
    reasonLabel: 'What it is for',
    placeholder: 'e.g. Replacement ID card',
  },
  correction: {
    title: 'Correct the amount',
    hint: 'The challan was billed wrongly. The fee income it carries is corrected in the books.',
    reasonLabel: 'What was wrong',
    placeholder: 'e.g. Transport billed for a month the student did not use it',
  },
};

/**
 * Change what a challan asks for: an extra charge, or a correction up or
 * down. A reduction never exceeds what is still owed, and above the school's
 * approval limit it is sent to Waive, where a second person decides it.
 */
const ChallanAdjustmentDialog: React.FC<{
  mode: ChallanAdjustmentMode | null;
  invoice: { _id: string; invoiceNumber: string; outstanding: number } | null;
  limits: FeeRequestLimits | null;
  onClose: () => void;
  onSaved: () => void;
  /** Opens Waive instead, carrying the amount and reason, for a reduction that needs a second person. */
  onWaiveInstead: (prefill: { amount: number; reason: string }) => void;
}> = ({ mode, invoice, limits, onClose, onSaved, onWaiveInstead }) => {
  const theme = useTheme();
  const { currency } = useAuth();
  const money = useCurrencyFormat();
  const { enqueueSnackbar } = useSnackbar();
  const [direction, setDirection] = useState<'down' | 'up'>('down');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Focus once the dialog has opened — autoFocus is lost when it opens from a closing menu.
  const amountRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!mode) return;
    setDirection('down');
    setAmount('');
    setReason('');
    setError(null);
  }, [mode]);

  if (!mode || !invoice) return null;
  const copy = COPY[mode];
  const reducing = mode === 'correction' && direction === 'down';
  const value = Number(amount) || 0;
  const maxOff = limits?.waiver ?? invoice.outstanding;
  const tooMuch = reducing && value > maxOff + 0.005;
  const needsApproval = reducing && !!limits?.requiresApproval && value > limits.approvalLimit + 0.005;
  const signed = reducing ? -value : value;
  const after = Math.max(0, invoice.outstanding + signed);
  const valid = value > 0 && !!reason.trim() && !tooMuch && !needsApproval;

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await feeInvoiceService.createAdjustment(invoice._id, {
        kind: mode === 'charge' ? 'ad_hoc_charge' : 'correction',
        amount: signed,
        reason: reason.trim(),
      });
      enqueueSnackbar(mode === 'charge' ? `${money(value)} charge added` : `Challan corrected by ${signed > 0 ? '+' : '−'}${money(value)}`, { variant: 'success' });
      onSaved();
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Could not save this.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth TransitionProps={{ onEntered: () => amountRef.current?.focus() }}>
      <DialogTitle component="div" sx={{ pb: 0.5 }}>
        <Typography variant="h6" fontWeight={700}>{copy.title}</Typography>
        <Typography variant="body2" color="text.secondary">Challan {invoice.invoiceNumber} · {copy.hint}</Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: '16px !important' }}>
        <Stack spacing={2}>
          {mode === 'correction' && (
            <ToggleButtonGroup
              exclusive fullWidth size="small" value={direction}
              onChange={(_, v) => v && setDirection(v)}
              sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 600, py: 0.9 } }}
            >
              <ToggleButton value="down">Reduce it</ToggleButton>
              <ToggleButton value="up">Raise it</ToggleButton>
            </ToggleButtonGroup>
          )}
          <TextField
            label={mode === 'charge' ? 'Charge' : reducing ? 'Take off' : 'Add'}
            size="small"
            value={amount}
            onChange={e => /^\d*(\.\d{0,2})?$/.test(e.target.value) && setAmount(e.target.value)}
            disabled={busy}
            inputRef={amountRef}
            error={tooMuch}
            helperText={reducing ? (tooMuch ? `Only ${money(maxOff)} is still owed` : `Up to ${money(maxOff)}`) : ' '}
            InputProps={{ startAdornment: <InputAdornment position="start">{currency}</InputAdornment> }}
            inputProps={{ inputMode: 'decimal' }}
          />
          <TextField
            label={copy.reasonLabel}
            size="small"
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder={copy.placeholder}
            multiline
            minRows={2}
            inputProps={{ maxLength: 500 }}
            disabled={busy}
          />

          {value > 0 && !tooMuch && (
            <Box sx={{ px: 1.75, py: 1.25, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.06) }}>
              <Stack direction="row" alignItems="center" spacing={1} justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Still owed</Typography>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>{money(invoice.outstanding)}</Typography>
                  <ArrowForward sx={{ fontSize: 16, color: 'text.disabled' }} />
                  <Typography variant="body2" fontWeight={700} sx={{ fontVariantNumeric: 'tabular-nums' }}>{money(after)}</Typography>
                </Stack>
              </Stack>
            </Box>
          )}

          {needsApproval && (
            <Alert
              severity="warning"
              variant="outlined"
              action={<Button color="inherit" size="small" onClick={() => { onClose(); onWaiveInstead({ amount: value, reason: reason.trim() }); }}>Waive instead</Button>}
            >
              Above {money(limits!.approvalLimit)}, money off a challan needs a second person. Waive it so it goes to Finance → Approvals.
            </Alert>
          )}
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={busy}>Cancel</Button>
        <Button variant="contained" disableElevation onClick={submit} disabled={!valid || busy}>
          {busy ? 'Saving…' : mode === 'charge' ? 'Add charge' : 'Save correction'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChallanAdjustmentDialog;
